/**
 * The audit page (TMMAPP-0022, AUD-02 / AUD-03; TMMAPP-0103, audit A4 §7, P:1695-1741) at `/me/audit`.
 * The "Money Tools" eyebrow (`PageHeader`, TMMAPP-0150 U2), a segmented OWNER REVIEW / PROTECTED REVIEW toggle (the owner's full
 * view, or one virtual account's redacted feed with a select to pick it), an outlined EXPORT CSV, a
 * scope banner, the filters (D1) and the v2 list (TMMAPP-0151 §J 27, `AuditList layout="list"`) under
 * the prototype's lead. A NONE / GUEST tier still reads their own actions — not a subscriber-only
 * surface.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shell/NotifyProvider';
import { accountsApi } from '@/api/accounts';
import { auditApi, CHANGE_LOG_KINDS, type AuditEvent } from '@/api/audit';
import { exportsApi } from '@/api/exports';
import { envelopesApi } from '@/api/envelopes';
import { virtualAccountsApi, type VirtualAccountDetail } from '@/api/virtualAccounts';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { ErrorState } from '@/components/ErrorState';
import { SegmentedControl } from '@/components/SegmentedControl';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import {
  activeCount,
  civilDateParam,
  defaultsOf,
  enumParam,
  intParam,
  stringParam,
  useUrlState,
} from '@/shared/useUrlState';
import { layerOverSurface, resolveTokens } from '@/theme/theme';
import { downloadBlob } from '@/utils/downloadBlob';
import { AuditList, type NameMaps } from './AuditList';
import { defaultAuditRange, validRange } from './dates';
import { ENTITY_TYPES } from './labels';

const PAGE_SIZE = 50;
const SKELETON_KEYS = ['a', 'b', 'c', 'd', 'e'] as const;
const FILTERS = ['from', 'to', 'action', 'entityType'] as const;
// The date fields keep the selects' height: Chrome's date picker indicator grows the input a pixel.
const DATE_FIELD_SX = {
  flex: { md: '0 0 160px' },
  '& input[type="date"]': { height: '1.4375em' },
} as const;
/**
 * A change-log kind (TMMAPI-0148) in the action filter, asked of the server: the protected review's
 * `kinds`, and the owner review's too since TMMAPI-0152 (ask 6).
 */
const KIND_PREFIX = 'kind:';

type AuditView = 'owner' | 'protected';

type PageState =
  | { key: string; status: 'ready'; rows: AuditEvent[]; nextCursor: string | null }
  | { key: string; status: 'error'; error: unknown };

/**
 * The loaded rows a filter keeps. The kind is asked of the server in both views (`kinds`); the owner
 * view asks for the entity type too, so its rows already hold. The protected endpoint has no entity
 * filter: there an entity type narrows what has loaded.
 */
function filterLoaded(
  rows: readonly AuditEvent[],
  view: AuditView,
  entityType: string,
): AuditEvent[] {
  if (view === 'owner' || entityType.length === 0) return [...rows];
  return rows.filter((row) => row.entityType === entityType);
}

export function AuditPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const tokens = resolveTokens(useTheme());
  const title = usePageTitle({ title: t('toolsNav.audit') });

  const zone = me.user.timezone ?? 'UTC';
  // The default range is the last 30 days in the person's zone, so it is omitted from the URL.
  const schema = useMemo(() => {
    const range = defaultAuditRange(zone);
    return {
      view: enumParam<AuditView>(['owner', 'protected'], 'owner'),
      va: intParam(0, { min: 0 }),
      from: civilDateParam(range.from),
      to: civilDateParam(range.to),
      action: stringParam(),
      entityType: stringParam(),
    };
  }, [zone]);
  const [filters, setUrl] = useUrlState(schema);
  const { view, from, to, action, entityType } = filters;
  // An old link's action or group (the owner review's, before ask 6) names no kind: every kind.
  const kind = CHANGE_LOG_KINDS.find((each) => action === `${KIND_PREFIX}${each}`);
  const rangeOk = validRange(from, to);

  // The protected review reads one virtual account: the URL's, else the first one the person can see.
  const choices = me.virtualAccounts;
  const virtualAccountId =
    view === 'protected'
      ? (choices.find((row) => row.id === filters.va)?.id ?? choices[0]?.id ?? null)
      : null;

  const [tick, setTick] = useState(0);
  const [page, setPage] = useState<PageState | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [accountNames, setAccountNames] = useState<Record<number, string>>({});
  const [envelopeNames, setEnvelopeNames] = useState<Record<number, string>>({});
  const [envelopeLookups, setEnvelopeLookups] = useState<ReadonlySet<number>>(() => new Set());
  const [detail, setDetail] = useState<VirtualAccountDetail | null>(null);
  const notify = useNotify();
  const [exporting, setExporting] = useState(false);
  const lastKey = useRef<string | null>(null);
  const seq = useRef(0);

  const needsVirtualAccount = view === 'protected';
  const key =
    rangeOk && (!needsVirtualAccount || virtualAccountId !== null)
      ? `${view}|${virtualAccountId ?? ''}|${from}|${to}|${action}|${entityType}|${tick}`
      : null;

  const fetchPage = useCallback(
    (cursor?: string) =>
      view === 'owner'
        ? auditApi.listMine({
            from,
            to,
            timeZone: zone,
            ...(kind !== undefined ? { kinds: [kind] } : {}),
            ...(entityType.length > 0 ? { entityType } : {}),
            ...(cursor !== undefined ? { cursor } : {}),
            limit: PAGE_SIZE,
          })
        : auditApi.listVirtualAccount(virtualAccountId ?? 0, {
            from,
            to,
            timeZone: zone,
            ...(kind !== undefined ? { kinds: [kind] } : {}),
            ...(cursor !== undefined ? { cursor } : {}),
          }),
    [view, virtualAccountId, from, to, zone, entityType, kind],
  );

  const load = useCallback(async () => {
    if (key === null) return;
    const mine = ++seq.current;
    try {
      const result = await fetchPage();
      if (seq.current !== mine) return;
      setPage({ key, status: 'ready', rows: result.items, nextCursor: result.nextCursor });
    } catch (error) {
      if (seq.current !== mine) return;
      setPage({ key, status: 'error', error });
    }
  }, [key, fetchPage]);

  useEffect(() => {
    if (key === null || lastKey.current === key) return;
    lastKey.current = key;
    void load();
  }, [key, load]);

  useEffect(() => {
    let cancelled = false;
    void accountsApi
      .list()
      .then((rows) => {
        if (cancelled) return;
        setAccountNames(Object.fromEntries(rows.map((row) => [row.id, row.name])));
      })
      .catch(() => {
        /* an unnamed account reads as Unavailable */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The protected review names its envelopes and amounts in the virtual account's own terms.
  useEffect(() => {
    if (view !== 'protected' || virtualAccountId === null) return;
    let cancelled = false;
    void virtualAccountsApi
      .get(virtualAccountId)
      .then((row) => {
        if (!cancelled) setDetail(row);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [view, virtualAccountId]);

  const ready = page !== null && page.key === key && page.status === 'ready' ? page : null;

  useEffect(() => {
    if (ready === null || view !== 'owner') return;
    const virtualAccountIds = [
      ...new Set(
        ready.rows.flatMap((row) =>
          row.envelopeId !== null &&
          envelopeNames[row.envelopeId] === undefined &&
          row.virtualAccountId !== null &&
          !envelopeLookups.has(row.virtualAccountId)
            ? [row.virtualAccountId]
            : [],
        ),
      ),
    ];
    if (virtualAccountIds.length === 0) return;
    let cancelled = false;
    void (async () => {
      const found: Record<number, string> = {};
      await Promise.all(
        virtualAccountIds.map(async (id) => {
          try {
            const list = await envelopesApi.listForVirtualAccount(id, { includeArchived: true });
            for (const envelope of list) found[envelope.id] = envelope.name;
          } catch {
            /* stay unavailable */
          }
        }),
      );
      if (cancelled) return;
      if (Object.keys(found).length > 0) setEnvelopeNames((prev) => ({ ...prev, ...found }));
      setEnvelopeLookups((prev) => {
        const next = new Set(prev);
        for (const id of virtualAccountIds) next.add(id);
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, view, envelopeNames, envelopeLookups]);
  const failed = page !== null && page.key === key && page.status === 'error' ? page : null;
  const loading = key !== null && ready === null && failed === null;

  const dateOr = (value: string, fallback: string) => (value === '' ? fallback : value);

  async function handleLoadMore() {
    if (ready === null || ready.nextCursor === null || loadingMore) return;
    const startKey = ready.key;
    setLoadingMore(true);
    try {
      const next = await fetchPage(ready.nextCursor);
      setPage((prev) =>
        prev !== null && prev.status === 'ready' && prev.key === startKey
          ? { ...prev, rows: [...prev.rows, ...next.items], nextCursor: next.nextCursor }
          : prev,
      );
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleExport() {
    if (!rangeOk || exporting) return;
    setExporting(true);
    try {
      const { blob, filename } =
        view === 'owner'
          ? await exportsApi.myAuditCsv({ from, to, timeZone: zone })
          : await exportsApi.virtualAccountAuditCsv(virtualAccountId ?? 0, {
              from,
              to,
              timeZone: zone,
            });
      downloadBlob(blob, filename ?? 'audit.csv');
      notify(t('exports.ready'));
    } catch {
      notify(t('exports.failed'), { severity: 'error' });
    } finally {
      setExporting(false);
    }
  }

  // A detail left from the previous selection is not this virtual account's.
  const current = detail !== null && detail.id === virtualAccountId ? detail : null;
  const virtualAccountName =
    current?.name ?? choices.find((row) => row.id === virtualAccountId)?.name ?? '';
  const names: NameMaps =
    view === 'owner'
      ? {
          virtualAccounts: Object.fromEntries(me.virtualAccounts.map((d) => [d.id, d.name])),
          accounts: accountNames,
          envelopes: envelopeNames,
        }
      : {
          virtualAccounts:
            virtualAccountId === null ? {} : { [virtualAccountId]: virtualAccountName },
          accounts: {},
          envelopes: Object.fromEntries(
            (current?.envelopes ?? []).map((envelope) => [envelope.id, envelope.name]),
          ),
        };
  const ctx = {
    locale,
    timeZone: zone,
    currency: view === 'protected' && current !== null ? current.currency : me.user.currency,
  };
  const shown = ready === null ? [] : filterLoaded(ready.rows, view, entityType);
  const bannerBg = layerOverSurface(tokens, view === 'owner' ? tokens.hover : tokens.selected);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        title={title}
        subtitle={t('audit.lead')}
        actions={
          <>
            <SegmentedControl<AuditView>
              aria-label={t('audit.viewLabel')}
              value={view}
              options={[
                { value: 'owner', label: t('audit.ownerReview') },
                { value: 'protected', label: t('audit.protectedReview') },
              ]}
              // Both views filter by the same kinds (TMMAPI-0152 ask 6): the choice carries over.
              onChange={(next) => setUrl({ view: next })}
            />
            {view === 'protected' && choices.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="audit-virtual-account">{t('audit.virtualAccount')}</InputLabel>
                <Select
                  labelId="audit-virtual-account"
                  value={virtualAccountId === null ? '' : String(virtualAccountId)}
                  onChange={(event) => setUrl({ va: Number(event.target.value) })}
                >
                  {choices.map((row) => (
                    <MenuItem key={row.id} value={String(row.id)}>
                      {row.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Tooltip title={t('audit.exportRangeOnly')} describeChild>
              <span>
                <Button
                  variant="outlined"
                  onClick={() => void handleExport()}
                  disabled={!rangeOk || exporting || key === null}
                >
                  {t('audit.exportCsv')}
                </Button>
              </span>
            </Tooltip>
          </>
        }
      />

      <Box
        data-audit-banner={view}
        sx={{
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          background: bannerBg,
          borderRadius: `${tokens.radius.control}px`,
          p: '12px 16px',
          fontSize: 13,
        }}
      >
        <Typography component="span" sx={{ fontWeight: 500, fontSize: 13, color: tokens.text }}>
          {view === 'owner'
            ? t('audit.banner.ownerTitle')
            : t('audit.banner.protectedTitle', { name: virtualAccountName })}
        </Typography>
        <Typography component="span" sx={{ fontSize: 13, color: tokens.text2, flex: '1 1 320px' }}>
          {view === 'owner'
            ? t('audit.banner.ownerBody')
            : t('audit.banner.protectedBody', { name: virtualAccountName })}
        </Typography>
      </Box>

      <Box>
        <Box
          data-audit-filters=""
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'flex-end' },
            gap: 2,
          }}
        >
          <TextField
            type="date"
            size="small"
            label={t('audit.from')}
            value={from}
            onChange={(event) => setUrl({ from: dateOr(event.target.value, schema.from.default) })}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={DATE_FIELD_SX}
          />
          <TextField
            type="date"
            size="small"
            label={t('audit.to')}
            value={to}
            onChange={(event) => setUrl({ to: dateOr(event.target.value, schema.to.default) })}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={DATE_FIELD_SX}
          />
          <FormControl size="small" sx={{ flex: { md: '1 1 220px' }, minWidth: 0 }}>
            <InputLabel id="audit-action" shrink>
              {t('audit.actionLabel')}
            </InputLabel>
            <Select
              labelId="audit-action"
              label={t('audit.actionLabel')}
              notched
              displayEmpty
              value={kind !== undefined ? `${KIND_PREFIX}${kind}` : ''}
              onChange={(event) => setUrl({ action: String(event.target.value) })}
              {...(view === 'protected' ? { 'aria-describedby': 'audit-filter-help' } : {})}
            >
              <MenuItem value="">{t('audit.anyAction')}</MenuItem>
              {CHANGE_LOG_KINDS.map((each) => (
                <MenuItem key={each} value={`${KIND_PREFIX}${each}`}>
                  {t(`changeLog.kind.${each}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: { md: '1 1 200px' }, minWidth: 0 }}>
            <InputLabel id="audit-entity" shrink>
              {t('audit.entityLabel')}
            </InputLabel>
            <Select
              labelId="audit-entity"
              label={t('audit.entityLabel')}
              notched
              displayEmpty
              value={entityType}
              onChange={(event) => setUrl({ entityType: String(event.target.value) })}
              {...(view === 'protected' ? { 'aria-describedby': 'audit-filter-help' } : {})}
            >
              <MenuItem value="">{t('audit.anyEntity')}</MenuItem>
              {ENTITY_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`audit.entity.${type}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ClearFiltersButton
            active={activeCount(schema, filters, FILTERS)}
            onClear={() => setUrl(defaultsOf(schema, FILTERS))}
          />
        </Box>
        {view === 'protected' && (
          <Typography id="audit-filter-help" sx={{ fontSize: 12, color: tokens.text2, mt: 0.75 }}>
            {t('audit.protectedFilterHelp')}
          </Typography>
        )}
      </Box>

      {view === 'protected' && choices.length === 0 && (
        <Typography sx={{ fontSize: 13, color: tokens.text2 }}>
          {t('audit.noVirtualAccounts')}
        </Typography>
      )}

      {failed !== null && (
        <ErrorState
          variant="section"
          error={failed.error}
          what={t('errors.what.audit')}
          onRetry={() => {
            lastKey.current = null;
            setTick((n) => n + 1);
          }}
        />
      )}

      {loading && (
        <Stack spacing={1}>
          {SKELETON_KEYS.map((k) => (
            <Skeleton key={k} variant="rounded" height={48} />
          ))}
        </Stack>
      )}

      {ready !== null && (
        <AuditList
          rows={shown}
          nextCursor={ready.nextCursor}
          loadingMore={loadingMore}
          onLoadMore={() => void handleLoadMore()}
          meUserId={me.user.id}
          mode={view}
          {...(view === 'protected' && virtualAccountId !== null
            ? { currentVirtualAccountId: virtualAccountId }
            : {})}
          names={names}
          ctx={ctx}
          layout="list"
          footnote={view === 'owner' ? t('audit.footnote.owner') : t('audit.footnote.protected')}
          emptyText={
            view === 'owner'
              ? t('audit.emptyOwner')
              : t('audit.emptyProtected', { name: virtualAccountName })
          }
        />
      )}
    </Box>
  );
}
